import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Truck, Package, MapPin, Search, Clock, CheckCircle, 
  Circle, User, Phone, ArrowLeft
} from 'lucide-react';
import { toast } from '../hooks/use-toast';
import api from '../utils/api';

const LogisticsTrack = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { trackingCode: paramCode } = useParams();
  
  const [trackingCode, setTrackingCode] = useState(
    paramCode || location.state?.trackingCode || ''
  );
  const [loading, setLoading] = useState(false);
  const [shipment, setShipment] = useState(null);

  useEffect(() => {
    if (trackingCode) {
      handleTrack();
    }
  }, []);

  const handleTrack = async () => {
    if (!trackingCode.trim()) {
      toast({
        title: 'Enter Tracking Code',
        description: 'Please enter a valid tracking code',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);
    try {
      const response = await api.get(`/logistics/track/${trackingCode.trim()}`);
      setShipment(response.data.shipment);
    } catch (error) {
      toast({
        title: 'Shipment Not Found',
        description: 'Please check the tracking code and try again',
        variant: 'destructive'
      });
      setShipment(null);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'text-yellow-500';
      case 'accepted': return 'text-blue-500';
      case 'pickup_en_route': return 'text-blue-600';
      case 'goods_collected': return 'text-purple-500';
      case 'in_transit': return 'text-orange-500';
      case 'delivered': return 'text-green-500';
      case 'cancelled': return 'text-red-500';
      default: return 'text-gray-500';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending': return 'Awaiting Operator';
      case 'accepted': return 'Operator Assigned';
      case 'pickup_en_route': return 'Operator En Route';
      case 'goods_collected': return 'Goods Collected';
      case 'in_transit': return 'In Transit';
      case 'delivered': return 'Delivered';
      case 'cancelled': return 'Cancelled';
      default: return status;
    }
  };

  const statusSteps = [
    { key: 'pending', label: 'Booked' },
    { key: 'accepted', label: 'Operator Assigned' },
    { key: 'pickup_en_route', label: 'En Route to Pickup' },
    { key: 'goods_collected', label: 'Goods Collected' },
    { key: 'in_transit', label: 'In Transit' },
    { key: 'delivered', label: 'Delivered' }
  ];

  const getCurrentStepIndex = () => {
    if (!shipment) return -1;
    const idx = statusSteps.findIndex(s => s.key === shipment.status);
    return idx >= 0 ? idx : -1;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-white p-6">
        <div className="max-w-4xl mx-auto">
          <button 
            onClick={() => navigate('/logistics')}
            className="flex items-center gap-2 text-orange-100 hover:text-white mb-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Search className="h-6 w-6" />
            Track Shipment
          </h1>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4 space-y-4">
        {/* Search Box */}
        <Card className="p-4">
          <div className="flex gap-2">
            <Input
              value={trackingCode}
              onChange={(e) => setTrackingCode(e.target.value.toUpperCase())}
              placeholder="Enter tracking code (e.g., TK-ABC123)"
              className="flex-1 font-mono text-lg"
              data-testid="tracking-input"
            />
            <Button 
              onClick={handleTrack}
              disabled={loading}
              className="bg-orange-500 hover:bg-orange-600"
              data-testid="track-btn"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              ) : (
                <Search className="h-4 w-4" />
              )}
            </Button>
          </div>
        </Card>

        {/* Shipment Details */}
        {shipment && (
          <>
            {/* Status Card */}
            <Card className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <p className="text-sm text-gray-500">Tracking Code</p>
                  <p className="text-2xl font-mono font-bold">{shipment.trackingCode}</p>
                </div>
                <div className={`px-4 py-2 rounded-full ${
                  shipment.status === 'delivered' ? 'bg-green-100' : 
                  shipment.status === 'cancelled' ? 'bg-red-100' : 'bg-orange-100'
                }`}>
                  <span className={`font-semibold ${getStatusColor(shipment.status)}`}>
                    {getStatusLabel(shipment.status)}
                  </span>
                </div>
              </div>

              {/* Progress Steps */}
              {shipment.status !== 'cancelled' && (
                <div className="relative">
                  <div className="flex justify-between mb-2">
                    {statusSteps.map((step, idx) => {
                      const currentIdx = getCurrentStepIndex();
                      const isCompleted = idx <= currentIdx;
                      const isCurrent = idx === currentIdx;
                      
                      return (
                        <div key={step.key} className="flex flex-col items-center flex-1">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center z-10 ${
                            isCompleted 
                              ? 'bg-orange-500 text-white' 
                              : 'bg-gray-200 text-gray-400'
                          } ${isCurrent ? 'ring-4 ring-orange-200' : ''}`}>
                            {isCompleted ? (
                              <CheckCircle className="h-5 w-5" />
                            ) : (
                              <Circle className="h-5 w-5" />
                            )}
                          </div>
                          <p className={`text-xs mt-2 text-center ${
                            isCompleted ? 'text-orange-600 font-medium' : 'text-gray-400'
                          }`}>
                            {step.label}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                  {/* Progress Line */}
                  <div className="absolute top-4 left-0 right-0 h-0.5 bg-gray-200 -z-0">
                    <div 
                      className="h-full bg-orange-500 transition-all"
                      style={{ width: `${(getCurrentStepIndex() / (statusSteps.length - 1)) * 100}%` }}
                    />
                  </div>
                </div>
              )}
            </Card>

            {/* Route Info */}
            <Card className="p-4">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <MapPin className="h-5 w-5 text-orange-500" />
                Route
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-3 h-3 rounded-full bg-green-500 mt-1.5" />
                  <div>
                    <p className="text-xs text-gray-500">PICKUP</p>
                    <p className="font-medium">{shipment.pickupAddress}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-3 h-3 rounded-full bg-red-500 mt-1.5" />
                  <div>
                    <p className="text-xs text-gray-500">DELIVERY</p>
                    <p className="font-medium">{shipment.dropoffAddress}</p>
                  </div>
                </div>
              </div>
            </Card>

            {/* Cargo Info */}
            <Card className="p-4">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <Package className="h-5 w-5 text-orange-500" />
                Cargo Details
              </h3>
              
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Description</span>
                  <span className="font-medium">{shipment.cargo?.description}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Category</span>
                  <span className="font-medium capitalize">{shipment.cargo?.category}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Vehicle Type</span>
                  <span className="font-medium capitalize">{shipment.vehicleType?.replace('_', ' ')}</span>
                </div>
              </div>
            </Card>

            {/* Operator Info */}
            {shipment.operator && (
              <Card className="p-4">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Truck className="h-5 w-5 text-orange-500" />
                  Operator
                </h3>
                
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                    <User className="h-6 w-6 text-orange-500" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium">{shipment.operator.name}</p>
                    <p className="text-sm text-gray-500">
                      {shipment.operator.vehicleMake} {shipment.operator.vehicleModel}
                    </p>
                  </div>
                  <Button variant="outline" size="sm">
                    <Phone className="h-4 w-4 mr-1" />
                    Call
                  </Button>
                </div>
              </Card>
            )}

            {/* Status History */}
            <Card className="p-4">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <Clock className="h-5 w-5 text-orange-500" />
                Status History
              </h3>
              
              <div className="space-y-3">
                {shipment.statusHistory?.map((entry, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <div className={`w-2 h-2 rounded-full mt-2 ${
                      idx === 0 ? 'bg-orange-500' : 'bg-gray-300'
                    }`} />
                    <div className="flex-1">
                      <p className="font-medium capitalize">
                        {entry.status?.replace('_', ' ')}
                      </p>
                      <p className="text-xs text-gray-500">{entry.notes}</p>
                      <p className="text-xs text-gray-400">
                        {new Date(entry.timestamp).toLocaleString()}
                      </p>
                    </div>
                  </div>
                )).reverse()}
              </div>
            </Card>
          </>
        )}

        {/* No Shipment Found */}
        {!shipment && !loading && trackingCode && (
          <Card className="p-8 text-center">
            <Package className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No shipment found for this tracking code</p>
            <p className="text-sm text-gray-400 mt-1">Please check and try again</p>
          </Card>
        )}
      </div>
    </div>
  );
};

export default LogisticsTrack;
